import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, MagnifyingGlass, Trash, CheckCircle, Receipt, Warning, ArrowClockwise, Barcode } from "@phosphor-icons/react";
import Thumb from "@/components/Thumb";
import SaleReceipt from "@/components/SaleReceipt";
import CustomerPicker from "@/components/CustomerPicker";
import { fetchPrimaryAttachments, bulkPrimaryMap } from "@/lib/attachments";

const inputCls = "w-full bg-transparent border-b py-2 outline-none focus:border-[#C2A878] text-sm";

function useList(url, deps = []) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    if (!url) return;
    try { setRows((await api.get(url)).data); }
    catch (e) { if (![403, 404].includes(e?.response?.status)) toast.error(formatApiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, deps);
  return [rows, load];
}

function StatusPill({ status, tone = "gold", testId }) {
  const map = { gold: "var(--nylsu-gold)", warn: "var(--nylsu-warning)", danger: "var(--nylsu-danger)", success: "var(--nylsu-success)", muted: "#8F8D84" };
  return <span data-testid={testId} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border" style={{ borderColor: map[tone], color: map[tone] }}>{status}</span>;
}

function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
      <div>
        <div className="overline mb-3">{eyebrow}</div>
        <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-none">{title}</h1>
        {subtitle && <p className="mt-4 text-sm text-[#5C5A54] max-w-xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

function saleStatusTone(s) {
  return { draft: "muted", completed: "success", voided: "danger" }[s] || "muted";
}
function payStatusTone(s) {
  return { unpaid: "danger", partial: "warn", paid: "success", refunded: "muted", partially_refunded: "warn" }[s] || "muted";
}
function invStatusTone(s) {
  return { synced: "success", pending: "warn", processing: "warn", retrying: "warn", failed: "danger", not_required: "muted" }[s] || "muted";
}

/* ═════════════ POS — New Sale ═════════════ */
export function POSPage() {
  const { user } = useAuth();
  const { t, tEnum, fmtCurrency } = useI18n();
  const nav = useNavigate();
  const [locations] = useList("/locations");
  const [styles] = useList("/styles");
  const [inv] = useList("/inventory");
  const [sale, setSale] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [locationId, setLocationId] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [primaryMap, setPrimaryMap] = useState({});
  const [discountPct, setDiscountPct] = useState(0);
  const [discountReason, setDiscountReason] = useState("");
  const [payment, setPayment] = useState({ method: "credit_card", amount: 0 });

  useEffect(() => {
    if (!locationId && locations[0]) setLocationId(locations[0].id);
  }, [locations]);
  useEffect(() => {
    if (styles.length) fetchPrimaryAttachments("style", styles.map((s) => s.id)).then((l) => setPrimaryMap(bulkPrimaryMap(l)));
  }, [styles.length]);

  // Flatten style variants for search
  const variants = useMemo(() => {
    const rows = [];
    for (const s of styles) {
      for (const v of s.variants || []) {
        rows.push({ style: s, variant: v });
      }
    }
    return rows;
  }, [styles]);
  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return variants.slice(0, 20);
    return variants.filter(({ style, variant }) =>
      style.style_code?.toLowerCase().includes(q) ||
      style.name?.toLowerCase().includes(q) ||
      variant.sku?.toLowerCase().includes(q) ||
      variant.color?.toLowerCase().includes(q) ||
      variant.size?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [variants, searchQ]);

  const stockOf = (sku) => inv.filter((r) => r.sku === sku && r.location_id === locationId).reduce((a, b) => a + b.on_hand, 0);

  // ─── Barcode scan ────────────────────────────────────────────────
  const searchInputRef = useRef(null);
  const [lastScan, setLastScan] = useState(null); // {sku, style_name, color, size, barcode, image_url, price, currency, on_hand}
  const scanLookup = async (raw) => {
    const q = (raw || "").trim();
    if (!q) return;
    try {
      const r = await api.get("/variants/lookup", { params: { barcode: q, location_id: locationId } });
      const hit = r.data;
      const stockHere = hit?.stock?.location_on_hand ?? 0;
      if (stockHere <= 0) {
        toast.error(t("pos.scanOutOfStock", { name: hit.style_name || hit.sku }));
        setLastScan({ ...hit, on_hand: 0 });
        return;
      }
      // Ensure sale exists
      const s = await ensureSale();
      if (!s) return;
      // Refetch to get latest items (state may be stale between rapid consecutive scans)
      const fresh = (await api.get(`/sales/${s.id}`)).data;
      const existing = (fresh.items || []).find((it) => it.sku === hit.sku);
      if (existing) {
        const nextQty = Number(existing.quantity || 0) + 1;
        if (nextQty > stockHere) {
          toast.error(t("pos.scanOutOfStock", { name: hit.style_name }));
          return;
        }
        await api.patch(`/sales/${s.id}/items/${existing.id}`, { quantity: nextQty });
      } else {
        await api.post(`/sales/${s.id}/items`, {
          style_id: hit.style_id, sku: hit.sku, color: hit.color, size: hit.size,
          quantity: 1, unit_price: hit.retail_price,
        });
      }
      setLastScan({ ...hit, on_hand: stockHere });
      toast.success(t("pos.scanAdded", { name: hit.style_name, sku: hit.sku }));
      await refreshSale(s.id);
      setSearchQ("");
      // Refocus for next scan
      searchInputRef.current?.focus();
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404) toast.error(t("pos.scanNotFound", { code: q }));
      else if (st === 410) toast.error(t("pos.scanInactive"));
      else toast.error(formatApiError(e));
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      scanLookup(searchQ);
    }
  };

  const ensureSale = async () => {
    if (sale) return sale;
    if (!locationId) { toast.error(t("pos.needLocation")); return null; }
    const r = await api.post("/sales", { location_id: locationId, channel: "showroom", walk_in: true, currency: "EUR" });
    setSale(r.data);
    return r.data;
  };

  const addItem = async ({ style, variant }) => {
    const av = stockOf(variant.sku);
    if (av <= 0) { toast.error(t("pos.outOfStock")); return; }
    const s = await ensureSale();
    if (!s) return;
    try {
      const r = await api.post(`/sales/${s.id}/items`, {
        style_id: style.id, sku: variant.sku, color: variant.color, size: variant.size,
        quantity: 1, unit_price: style.retail_price,
      });
      setSale({ ...s, ...r.data.totals, items: [...(s.items || []), r.data.item] });
      await refreshSale(s.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const refreshSale = async (sid) => {
    const r = await api.get(`/sales/${sid}`);
    setSale(r.data);
  };

  const updateItemQty = async (item, qty) => {
    try {
      const r = await api.patch(`/sales/${sale.id}/items/${item.id}`, { quantity: Number(qty) });
      setSale({ ...sale, ...r.data.totals });
      await refreshSale(sale.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeItem = async (item) => {
    try {
      await api.delete(`/sales/${sale.id}/items/${item.id}`);
      await refreshSale(sale.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyCustomer = async (customer) => {
    const s = await ensureSale();
    if (!s) return;
    try {
      await api.patch(`/sales/${s.id}`, {
        customer_id: customer?.id || null,
        walk_in: !customer,
      });
      setSelectedCustomer(customer || null);
      await refreshSale(s.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyDiscount = async () => {
    if (!sale) return;
    try {
      const r = await api.post(`/sales/${sale.id}/discount`, {
        sale_discount_percent: Number(discountPct), reason: discountReason,
      });
      setSale({ ...sale, ...r.data.totals });
      await refreshSale(sale.id);
      toast.success(t("pos.discountApplied"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addPayment = async () => {
    if (!sale) return;
    if (!(payment.amount > 0)) { toast.error(t("pos.paymentAmountRequired")); return; }
    try {
      const r = await api.post(`/sales/${sale.id}/payments`, payment);
      setSale({ ...sale, ...r.data.totals });
      await refreshSale(sale.id);
      setPayment({ ...payment, amount: 0 });
      toast.success(t("pos.paymentRecorded"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [split, setSplit] = useState({ card: 0, cash: 0, bank: 0 });
  const balance = Number(sale?.balance_due || 0);
  const splitTotal = Number(split.card || 0) + Number(split.cash || 0) + Number(split.bank || 0);

  const presetHalf = () => setSplit({ card: +(balance / 2).toFixed(2), cash: +(balance / 2).toFixed(2), bank: 0 });
  const presetCard = () => setSplit({ card: balance, cash: 0, bank: 0 });
  const presetCash = () => setSplit({ card: 0, cash: balance, bank: 0 });
  const recordSplit = async () => {
    if (!sale) return;
    if (splitTotal <= 0) { toast.error(t("pos.paymentAmountRequired")); return; }
    try {
      const posts = [];
      if (Number(split.card) > 0) posts.push({ method: "credit_card", amount: Number(split.card) });
      if (Number(split.cash) > 0) posts.push({ method: "cash", amount: Number(split.cash) });
      if (Number(split.bank) > 0) posts.push({ method: "bank_transfer", amount: Number(split.bank) });
      let totals;
      for (const p of posts) {
        const r = await api.post(`/sales/${sale.id}/payments`, p);
        totals = r.data.totals;
      }
      setSale({ ...sale, ...totals });
      await refreshSale(sale.id);
      setSplit({ card: 0, cash: 0, bank: 0 });
      toast.success(t("pos.splitRecorded", { n: posts.length }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeSale = async (force = false) => {
    try {
      const r = await api.post(`/sales/${sale.id}/complete`, { force });
      toast.success(t("pos.saleCompleted", { number: r.data.sale_number }));
      nav(`/sales/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="pos-page">
      <PageHeader eyebrow={t("pos.eyebrow")} title={t("pos.title")} subtitle={t("pos.subtitle")}
        actions={
          <div className="flex items-center gap-3">
            <select data-testid="pos-location" value={locationId} onChange={(e) => setLocationId(e.target.value)}
              className="border px-3 py-2 text-xs uppercase tracking-[0.15em]" style={{ borderColor: "var(--nylsu-border-strong)" }}>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        } />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_360px] gap-6">
        {/* LEFT — search + results */}
        <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="pos-search-panel">
          <div className="p-4 border-b" style={{ borderColor: "var(--nylsu-border)" }}>
            <div className="flex items-center gap-3 px-3 py-2 border" style={{ borderColor: "var(--nylsu-border)" }}>
              <Barcode size={16} weight="light" className="text-[#8F8D84]" />
              <input
                ref={searchInputRef}
                data-testid="pos-search-input"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={t("pos.searchScanPlaceholder")}
                className="flex-1 bg-transparent outline-none text-sm"
                autoFocus
              />
              <button
                type="button"
                onClick={() => scanLookup(searchQ)}
                disabled={!searchQ.trim()}
                className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border disabled:opacity-30"
                style={{ borderColor: "var(--nylsu-border-strong)" }}
                data-testid="pos-search-scan-btn"
              >
                {t("pos.scan")}
              </button>
            </div>
            {lastScan && (
              <div
                className="mt-3 p-2 border flex items-center gap-3 text-xs"
                style={{ borderColor: "var(--nylsu-gold)", background: "#FBF9F1" }}
                data-testid="pos-last-scan"
              >
                <Thumb attachment={primaryMap[lastScan.style_id]} size={40} alt={lastScan.style_name} />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-sm truncate">{lastScan.style_name}</div>
                  <div className="text-[#5C5A54]">{lastScan.color} · {lastScan.size} · <span className="font-mono">{lastScan.sku}</span></div>
                  <div className="font-mono text-[10px] text-[#8F8D84]">{lastScan.barcode}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono">{fmtCurrency(lastScan.retail_price, lastScan.currency)}</div>
                  <div className="text-[10px]" style={{ color: lastScan.on_hand > 0 ? "var(--nylsu-success)" : "var(--nylsu-danger)" }}>
                    {lastScan.on_hand > 0 ? `${lastScan.on_hand} ${t("pos.available")}` : t("pos.outOfStock")}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-2 max-h-[70vh] overflow-y-auto">
            {filtered.length === 0 && <div className="p-8 text-center text-xs text-[#8F8D84]">{t("pos.noResults")}</div>}
            {filtered.map(({ style, variant }) => {
              const av = stockOf(variant.sku);
              return (
                <button key={variant.sku} type="button" onClick={() => addItem({ style, variant })}
                  disabled={av <= 0}
                  data-testid={`pos-add-${variant.sku}`}
                  className="w-full flex items-center gap-3 p-3 border-b hover:bg-[#F4F3ED] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-left"
                  style={{ borderColor: "var(--nylsu-border)" }}>
                  <Thumb attachment={primaryMap[style.id]} size={48} alt={style.name} />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-sm truncate">{style.name}</div>
                    <div className="text-xs text-[#5C5A54]">{variant.color} · {variant.size}</div>
                    <div className="font-mono text-[10px] text-[#8F8D84]">{variant.sku}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{fmtCurrency(style.retail_price)}</div>
                    <div className="text-xs" style={{ color: av > 0 ? "var(--nylsu-success)" : "var(--nylsu-danger)" }}>
                      {av > 0 ? `${av} ${t("pos.available")}` : t("pos.outOfStock")}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* MIDDLE — cart */}
        <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="pos-cart">
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--nylsu-border)" }}>
            <div>
              <div className="overline">{t("pos.currentSale")}</div>
              <div className="font-mono text-xs mt-1">{sale?.sale_number || t("pos.newSaleDraft")}</div>
            </div>
            <div className="text-xs text-[#8F8D84]">{sale?.items?.length || 0} {t("pos.lines")}</div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {!sale?.items?.length && <div className="p-8 text-center text-xs text-[#8F8D84]">{t("pos.cartEmpty")}</div>}
            {sale?.items?.map((it) => (
              <div key={it.id} className="p-4 border-b flex items-center gap-3" style={{ borderColor: "var(--nylsu-border)" }}
                data-testid={`pos-cart-item-${it.sku}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-sm truncate">{it.style_name}</div>
                  <div className="text-xs text-[#5C5A54]">{it.color} · {it.size}</div>
                  <div className="font-mono text-[10px] text-[#8F8D84]">{it.sku}</div>
                </div>
                <input type="number" min="1" data-testid={`pos-qty-${it.sku}`}
                  className="w-14 border py-1 px-2 text-center font-mono text-sm"
                  style={{ borderColor: "var(--nylsu-border)" }}
                  defaultValue={it.quantity}
                  onBlur={(e) => e.target.value != it.quantity && updateItemQty(it, e.target.value)} />
                <div className="w-24 text-right font-mono text-sm">{fmtCurrency(it.line_total ?? it.unit_price * it.quantity)}</div>
                <button type="button" onClick={() => removeItem(it)} data-testid={`pos-remove-${it.sku}`}
                  className="p-1 text-[#8F8D84] hover:text-[#8C3B3B]">
                  <Trash size={14} weight="light" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — customer + summary + payment */}
        <div className="space-y-4" data-testid="pos-right">
          <div className="border p-4" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
            <div className="overline mb-3">{t("orders.customer")}</div>
            <CustomerPicker
              selected={selectedCustomer}
              walkIn={!selectedCustomer}
              onSelect={applyCustomer}
            />
          </div>

          <div className="border p-4" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="pos-summary">
            <div className="overline mb-2">{t("pos.summary")}</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#8F8D84]">{t("pos.subtotal")}</span><span className="font-mono">{fmtCurrency(sale?.subtotal || 0)}</span></div>
              <div className="flex justify-between"><span className="text-[#8F8D84]">{t("pos.itemDiscount")}</span><span className="font-mono">−{fmtCurrency(sale?.item_discount_total || 0)}</span></div>
              <div className="flex justify-between"><span className="text-[#8F8D84]">{t("pos.saleDiscount")}</span><span className="font-mono">−{fmtCurrency(sale?.sale_discount_amount_effective || 0)}</span></div>
              <div className="flex justify-between border-t pt-2" style={{ borderColor: "var(--nylsu-border)" }}>
                <span className="font-serif">{t("pos.netSales")}</span><span className="font-mono">{fmtCurrency(sale?.net_sales || 0)}</span>
              </div>
              <div className="flex justify-between"><span className="text-[#8F8D84]">{t("pos.tax")}</span><span className="font-mono">{fmtCurrency(sale?.tax_total || 0)}</span></div>
              <div className="flex justify-between border-t pt-2 font-serif text-lg" style={{ borderColor: "var(--nylsu-border)" }}>
                <span>{t("pos.total")}</span>
                <span className="font-mono" data-testid="pos-total">{fmtCurrency(sale?.grand_total || 0)}</span>
              </div>
              <div className="flex justify-between text-xs pt-2"><span className="text-[#8F8D84]">{t("pos.amountPaid")}</span><span className="font-mono">{fmtCurrency(sale?.amount_paid || 0)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-[#8F8D84]">{t("pos.balanceDue")}</span><span className="font-mono" style={{ color: (sale?.balance_due || 0) > 0 ? "var(--nylsu-warning)" : "var(--nylsu-success)" }}>{fmtCurrency(sale?.balance_due || 0)}</span></div>
            </div>
          </div>

          <div className="border p-4" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="pos-discount-panel">
            <div className="overline mb-2">{t("pos.discount")}</div>
            <div className="grid grid-cols-[70px_1fr_auto] gap-2 items-end">
              <input type="number" min="0" max="100" step="0.5" data-testid="pos-discount-pct"
                className={inputCls} value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
              <input type="text" data-testid="pos-discount-reason" className={inputCls} placeholder={t("pos.reason")}
                value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} />
              <button type="button" onClick={applyDiscount} disabled={!sale}
                data-testid="pos-apply-discount"
                className="text-xs uppercase tracking-[0.15em] px-3 py-2 border" style={{ borderColor: "var(--nylsu-border-strong)" }}>
                {t("pos.apply")}
              </button>
            </div>
            <div className="text-[10px] text-[#8F8D84] mt-2">{t("pos.discountNote")}</div>
          </div>

          <div className="border p-4" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="pos-payment-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">{t("pos.payment")}</div>
              <div className="text-[10px] text-[#8F8D84]">{t("pos.balanceDue")}: <span className="font-mono">{fmtCurrency(balance)}</span></div>
            </div>

            {/* Split entry */}
            <div className="space-y-2 mb-3" data-testid="pos-split-panel">
              <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("payment.method.credit_card")}</div>
                <input type="number" step="0.01" min="0" data-testid="pos-split-card" className={inputCls}
                  value={split.card} onChange={(e) => setSplit({ ...split, card: e.target.value })} placeholder="0.00" />
              </div>
              <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("payment.method.cash")}</div>
                <input type="number" step="0.01" min="0" data-testid="pos-split-cash" className={inputCls}
                  value={split.cash} onChange={(e) => setSplit({ ...split, cash: e.target.value })} placeholder="0.00" />
              </div>
              <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("payment.method.bank_transfer")}</div>
                <input type="number" step="0.01" min="0" data-testid="pos-split-bank" className={inputCls}
                  value={split.bank} onChange={(e) => setSplit({ ...split, bank: e.target.value })} placeholder="0.00" />
              </div>
            </div>

            {/* Quick presets */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button type="button" onClick={presetCard} disabled={balance <= 0} data-testid="pos-preset-card"
                className="text-[10px] uppercase tracking-[0.15em] py-2 border hover:bg-[#F4F3ED] disabled:opacity-40"
                style={{ borderColor: "var(--nylsu-border-strong)" }}>
                {t("pos.preset.cardAll")}
              </button>
              <button type="button" onClick={presetHalf} disabled={balance <= 0} data-testid="pos-preset-half"
                className="text-[10px] uppercase tracking-[0.15em] py-2 border hover:bg-[#F4F3ED] disabled:opacity-40"
                style={{ borderColor: "var(--nylsu-border-strong)" }}>
                {t("pos.preset.halfHalf")}
              </button>
              <button type="button" onClick={presetCash} disabled={balance <= 0} data-testid="pos-preset-cash"
                className="text-[10px] uppercase tracking-[0.15em] py-2 border hover:bg-[#F4F3ED] disabled:opacity-40"
                style={{ borderColor: "var(--nylsu-border-strong)" }}>
                {t("pos.preset.cashAll")}
              </button>
            </div>

            <button type="button" onClick={recordSplit} disabled={!sale || splitTotal <= 0}
              data-testid="pos-record-split"
              className="w-full py-2 text-[10px] uppercase tracking-[0.2em] disabled:opacity-40"
              style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
              {t("pos.recordSplit")} · {fmtCurrency(splitTotal)}
            </button>

            {sale?.payments?.length > 0 && (
              <div className="mt-3 space-y-1">
                {sale.payments.map((p) => (
                  <div key={p.id} className="text-xs flex justify-between border-t pt-2" style={{ borderColor: "var(--nylsu-border)" }}>
                    <span>{tEnum("payment.method", p.method)}</span>
                    <span className="font-mono">{fmtCurrency(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="button" onClick={() => completeSale(false)} disabled={!sale?.items?.length}
            data-testid="pos-complete-btn"
            className="w-full py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-40"
            style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
            <CheckCircle size={14} className="inline-block mr-2" />{t("pos.completeSale")}
          </button>
          {(sale?.balance_due || 0) > 0 && (
            <button type="button" onClick={() => completeSale(true)}
              data-testid="pos-complete-force"
              className="w-full py-2 text-[10px] uppercase tracking-[0.2em] border"
              style={{ borderColor: "var(--nylsu-warning)", color: "var(--nylsu-warning)" }}>
              {t("pos.completePartial")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═════════════ Sales list ═════════════ */
export function SalesListPage({ channel }) {
  const { t, tEnum, fmtCurrency, fmtDateTime } = useI18n();
  const [rows] = useList(channel ? `/sales?channel=${channel}` : "/sales", [channel]);
  const [customers] = useList("/customers");
  const cMap = Object.fromEntries(customers.map((c) => [c.id, c]));
  const nav = useNavigate();
  return (
    <div data-testid={channel ? `sales-${channel}-page` : "sales-page"}>
      <PageHeader eyebrow={t("sales.eyebrow")} title={channel === "showroom" ? t("sales.showroomTitle") : t("sales.title")}
        subtitle={t("sales.subtitle")}
        actions={
          <Link to="/sales/new" data-testid="new-sale-btn"
            className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em]"
            style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
            <Plus size={14} /> {t("sales.new")}
          </Link>
        } />
      <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("sales.number")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.date")}</th>
              <th className="data-label px-6 py-3 text-left">{t("orders.customer")}</th>
              <th className="data-label px-6 py-3 text-left">{t("sales.channel")}</th>
              <th className="data-label px-6 py-3 text-right">{t("sales.units")}</th>
              <th className="data-label px-6 py-3 text-right">{t("pos.netSales")}</th>
              <th className="data-label px-6 py-3 text-right">{t("sales.grossProfit")}</th>
              <th className="data-label px-6 py-3 text-right">{t("sales.margin")}</th>
              <th className="data-label px-6 py-3 text-left">{t("pos.payment")}</th>
              <th className="data-label px-6 py-3 text-left">{t("sales.invoice")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="px-6 py-12 text-center text-xs text-[#8F8D84]">{t("sales.empty")}</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} className="border-t h-14 cursor-pointer hover:bg-[#F4F3ED]"
                style={{ borderColor: "var(--nylsu-border)" }}
                onClick={() => nav(`/sales/${s.id}`)}
                data-testid={`sale-row-${s.sale_number}`}>
                <td className="px-6 font-mono text-xs">{s.sale_number}</td>
                <td className="px-6 text-xs">{fmtDateTime(s.completed_at || s.created_at)}</td>
                <td className="px-6 text-xs">{cMap[s.customer_id]?.name || (s.walk_in ? t("pos.walkIn") : "—")}</td>
                <td className="px-6 text-xs uppercase tracking-[0.1em]">{tEnum("sale.channel", s.channel)}</td>
                <td className="px-6 text-right font-mono text-xs">{s.units_total}</td>
                <td className="px-6 text-right font-mono">{fmtCurrency(s.net_sales)}</td>
                <td className="px-6 text-right font-mono text-xs">{fmtCurrency(s.gross_profit)}</td>
                <td className="px-6 text-right font-mono text-xs" style={{ color: s.gross_margin < 30 ? "var(--nylsu-warning)" : undefined }}>{s.gross_margin?.toFixed(1)}%</td>
                <td className="px-6"><StatusPill status={tEnum("payment.status", s.payment_status)} tone={payStatusTone(s.payment_status)} /></td>
                <td className="px-6"><StatusPill status={tEnum("invoice.status", s.invoice_status || "pending")} tone={invStatusTone(s.invoice_status || "pending")} /></td>
                <td className="px-6"><StatusPill status={tEnum("sale.status", s.status)} tone={saleStatusTone(s.status)} testId={`sale-status-${s.sale_number}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export function AllSalesPage() { return <SalesListPage />; }
export function ShowroomSalesPage() { return <SalesListPage channel="showroom" />; }

/* ═════════════ Sale detail ═════════════ */
export function SaleDetailPage() {
  const { saleId } = useParams();
  const { can } = useAuth();
  const { t, tEnum, fmtCurrency, fmtDateTime } = useI18n();
  const [sale, setSale] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [location, setLocation] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/sales/${saleId}`);
      setSale(r.data);
      if (r.data.customer_id) {
        const c = await api.get(`/customers/${r.data.customer_id}`).catch(() => null);
        if (c) setCustomer(c.data);
      }
      if (r.data.location_id) {
        const locs = await api.get("/locations").catch(() => ({ data: [] }));
        setLocation(locs.data.find((l) => l.id === r.data.location_id) || null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [saleId]);

  const retry = async (jid) => {
    try { await api.post(`/integration-jobs/${jid}/retry`); toast.success(t("sales.retryQueued")); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const voidSale = async () => {
    if (!window.confirm(t("sales.voidConfirm"))) return;
    try { await api.post(`/sales/${saleId}/void`); toast.success(t("sales.voided")); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openReceipt = () => setShowReceipt(true);
  const printReceipt = () => setTimeout(() => window.print(), 250);

  if (!sale) return <div className="text-sm text-[#8F8D84]">{t("common.loading")}</div>;

  if (showReceipt) {
    return (
      <div>
        <div className="no-print mb-4 flex items-center gap-3">
          <button onClick={() => setShowReceipt(false)} data-testid="receipt-back"
            className="text-xs uppercase tracking-[0.15em] px-4 py-2 border" style={{ borderColor: "var(--nylsu-border-strong)" }}>
            ← {t("common.back")}
          </button>
          <button onClick={printReceipt} data-testid="receipt-print"
            className="text-xs uppercase tracking-[0.2em] px-5 py-2" style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
            {t("sales.print")}
          </button>
        </div>
        <SaleReceipt sale={sale} customer={customer} location={location} />
      </div>
    );
  }

  return (
    <div data-testid="sale-detail-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">{tEnum("sale.channel", sale.channel)} · {fmtDateTime(sale.completed_at || sale.created_at)}</div>
          <h1 className="font-serif text-4xl tracking-tight font-light">{sale.sale_number}</h1>
          <div className="mt-3 flex items-center gap-3">
            <StatusPill status={tEnum("sale.status", sale.status)} tone={saleStatusTone(sale.status)} testId="sale-status" />
            <StatusPill status={tEnum("payment.status", sale.payment_status)} tone={payStatusTone(sale.payment_status)} />
            <StatusPill status={"Paraşüt: " + tEnum("invoice.status", sale.invoice_status || "pending")} tone={invStatusTone(sale.invoice_status || "pending")} />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={openReceipt} data-testid="sale-print" className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border" style={{ borderColor: "var(--nylsu-border-strong)" }}>
            <Receipt size={14} /> {t("sales.viewReceipt")}
          </button>
          {sale.status !== "voided" && can("sale:edit") && (
            <button onClick={voidSale} data-testid="sale-void"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border"
              style={{ borderColor: "var(--nylsu-danger)", color: "var(--nylsu-danger)" }}>
              <X size={14} /> {t("sales.void")}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="border p-6" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
          <div className="overline mb-2">{t("orders.customer")}</div>
          <div className="font-serif text-2xl">{customer?.name || (sale.walk_in ? t("pos.walkIn") : "—")}</div>
          {customer && <div className="text-xs text-[#5C5A54] mt-2">{customer.email}</div>}
        </div>
        <div className="border p-6" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
          <div className="overline mb-2">{t("pos.total")}</div>
          <div className="font-serif text-3xl font-light">{fmtCurrency(sale.grand_total, sale.currency)}</div>
          <div className="text-xs text-[#5C5A54] mt-1">{t("sales.grossProfit")}: <span className="font-mono">{fmtCurrency(sale.gross_profit)}</span> ({sale.gross_margin?.toFixed(1)}%)</div>
        </div>
        <div className="border p-6" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
          <div className="overline mb-2">{t("pos.payment")}</div>
          <div className="font-serif text-lg">{fmtCurrency(sale.amount_paid)} / {fmtCurrency(sale.grand_total)}</div>
          <div className="text-xs mt-1" style={{ color: sale.balance_due > 0 ? "var(--nylsu-warning)" : "var(--nylsu-success)" }}>
            {sale.balance_due > 0 ? t("pos.balanceDue") + ": " + fmtCurrency(sale.balance_due) : t("sales.fullyPaid")}
          </div>
        </div>
      </div>

      <div className="border mb-8" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">SKU</th>
              <th className="data-label px-6 py-3 text-left">{t("common.name")}</th>
              <th className="data-label px-6 py-3 text-right">{t("orders.qty")}</th>
              <th className="data-label px-6 py-3 text-right">{t("orders.unitPrice")}</th>
              <th className="data-label px-6 py-3 text-right">{t("sales.discount")}</th>
              <th className="data-label px-6 py-3 text-right">{t("pos.netSales")}</th>
              <th className="data-label px-6 py-3 text-right">{t("sales.unitCost")}</th>
              <th className="data-label px-6 py-3 text-right">{t("sales.grossProfit")}</th>
            </tr>
          </thead>
          <tbody>
            {(sale.items || []).map((it) => (
              <tr key={it.id} className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }}>
                <td className="px-6 font-mono text-xs">{it.sku}</td>
                <td className="px-6 text-xs">{it.style_name} · {it.color} · {it.size}</td>
                <td className="px-6 text-right font-mono">{it.quantity}</td>
                <td className="px-6 text-right font-mono text-xs">{fmtCurrency(it.unit_price)}</td>
                <td className="px-6 text-right font-mono text-xs">−{fmtCurrency(it.line_discount_amount_effective || 0)}</td>
                <td className="px-6 text-right font-mono">{fmtCurrency(it.net || 0)}</td>
                <td className="px-6 text-right font-mono text-xs text-[#8F8D84]">{fmtCurrency(it.unit_cost_snapshot || 0)}</td>
                <td className="px-6 text-right font-mono text-xs" style={{ color: "var(--nylsu-success)" }}>{fmtCurrency(it.line_gross_profit || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sale.integration_jobs?.length > 0 && (
        <div className="border p-6 mb-8" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid="sale-integrations">
          <div className="overline mb-3">{t("sales.integrations")} <span className="text-[10px] text-[#8F8D84] normal-case">— DEMO / NOT CONNECTED</span></div>
          {sale.integration_jobs.map((j) => (
            <div key={j.id} className="flex items-center justify-between py-2 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
              <div>
                <div className="font-mono text-xs">{j.system} · {j.kind}</div>
                <div className="text-[10px] text-[#8F8D84]">{j.idempotency_key}</div>
                {j.error_message && <div className="text-xs text-[#8C3B3B] mt-1">{j.error_message}</div>}
                {j.external_ref && <div className="text-xs">{t("sales.invoiceId")}: <span className="font-mono">{j.external_ref}</span></div>}
              </div>
              <div className="flex items-center gap-3">
                <StatusPill status={j.status.toUpperCase()} tone={invStatusTone(j.status)} />
                {j.status === "failed" && can("integration_job:retry") && (
                  <button onClick={() => retry(j.id)} data-testid={`retry-${j.id}`}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] px-3 py-1 border"
                    style={{ borderColor: "var(--nylsu-border-strong)" }}>
                    <ArrowClockwise size={12} /> {t("sales.retry")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═════════════ Returns ═════════════ */
export function ReturnsPage() {
  const { t, tEnum, fmtCurrency, fmtDateTime } = useI18n();
  const [rows, reload] = useList("/returns");
  const [openNew, setOpenNew] = useState(false);
  return (
    <div data-testid="returns-page">
      <PageHeader eyebrow={t("returns.eyebrow")} title={t("returns.title")} subtitle={t("returns.subtitle")}
        actions={<button onClick={() => setOpenNew(true)} data-testid="new-return-btn"
          className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em]"
          style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
          <Plus size={14} /> {t("returns.new")}
        </button>} />
      <div className="border" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--nylsu-table-head)" }}>
              <th className="data-label px-6 py-3 text-left">{t("returns.number")}</th>
              <th className="data-label px-6 py-3 text-left">{t("sales.number")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.date")}</th>
              <th className="data-label px-6 py-3 text-left">{t("returns.reason")}</th>
              <th className="data-label px-6 py-3 text-right">{t("returns.refund")}</th>
              <th className="data-label px-6 py-3 text-left">{t("common.status")}</th>
              <th className="data-label px-6 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-xs text-[#8F8D84]">{t("returns.empty")}</td></tr>}
            {rows.map((r) => <ReturnRow key={r.id} r={r} onChanged={reload} t={t} tEnum={tEnum} fmtCurrency={fmtCurrency} fmtDateTime={fmtDateTime} />)}
          </tbody>
        </table>
      </div>
      {openNew && <NewReturnModal onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); reload(); }} />}
    </div>
  );
}

function ReturnRow({ r, onChanged, t, tEnum, fmtCurrency, fmtDateTime }) {
  const complete = async () => {
    try { await api.post(`/returns/${r.id}/complete`); toast.success(t("returns.completedToast")); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <tr className="border-t h-14" style={{ borderColor: "var(--nylsu-border)" }} data-testid={`return-row-${r.return_number}`}>
      <td className="px-6 font-mono text-xs">{r.return_number}</td>
      <td className="px-6 font-mono text-xs">
        <Link to={`/sales/${r.sale_id}`} className="link-underline">{r.sale_number}</Link>
      </td>
      <td className="px-6 text-xs">{fmtDateTime(r.created_at)}</td>
      <td className="px-6 text-xs">{r.reason}</td>
      <td className="px-6 text-right font-mono">{fmtCurrency(r.total_refund)}</td>
      <td className="px-6"><StatusPill status={r.status.replace(/_/g, " ").toUpperCase()} tone={r.status === "completed" ? "success" : "warn"} /></td>
      <td className="px-6 text-right">
        {r.status !== "completed" && (
          <button onClick={complete} data-testid={`complete-return-${r.return_number}`}
            className="text-xs uppercase tracking-[0.15em] link-underline">{t("returns.completeInspection")}</button>
        )}
      </td>
    </tr>
  );
}

function NewReturnModal({ onClose, onSaved }) {
  const { t } = useI18n();
  const [saleNumber, setSaleNumber] = useState("");
  const [sale, setSale] = useState(null);
  const [reason, setReason] = useState("Wrong Size");
  const [notes, setNotes] = useState("");
  const [selectedItems, setSelectedItems] = useState({}); // id -> {qty, disposition, refund_amount}

  const fetchSale = async () => {
    try {
      const list = await api.get("/sales");
      const s = list.data.find((x) => x.sale_number === saleNumber.trim());
      if (!s) { toast.error(t("returns.saleNotFound")); return; }
      const r = await api.get(`/sales/${s.id}`);
      setSale(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    const items = Object.entries(selectedItems).map(([sale_item_id, v]) => ({
      sale_item_id, quantity: Number(v.qty || 0),
      disposition: v.disposition || "sellable",
      refund_amount: v.refund_amount != null && v.refund_amount !== "" ? Number(v.refund_amount) : null,
    })).filter((x) => x.quantity > 0);
    if (items.length === 0) { toast.error(t("returns.pickAtLeastOne")); return; }
    try {
      await api.post("/returns", { sale_id: sale.id, reason, notes, items });
      toast.success(t("returns.createdToast"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-10" style={{ background: "rgba(26,26,26,0.4)" }} onClick={onClose}>
      <div className="w-full max-w-3xl mx-4 p-8" style={{ background: "var(--nylsu-card)", border: "1px solid var(--nylsu-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-serif text-2xl">{t("returns.new")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="new-return-form">
          <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
            <label className="block"><div className="data-label mb-2">{t("returns.originalSale")}</div>
              <input required data-testid="return-sale-number" className={inputCls} value={saleNumber}
                onChange={(e) => setSaleNumber(e.target.value)} placeholder="SAL-26-00001" />
            </label>
            <button type="button" onClick={fetchSale} data-testid="return-fetch-sale"
              className="text-xs uppercase tracking-[0.15em] px-4 py-2 border" style={{ borderColor: "var(--nylsu-border-strong)" }}>{t("returns.load")}</button>
          </div>
          {sale && (
            <>
              <div className="grid grid-cols-2 gap-6">
                <label className="block"><div className="data-label mb-2">{t("returns.reason")}</div>
                  <select data-testid="return-reason" className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)}>
                    {["Wrong Size", "Customer Preference", "Quality Issue", "Damaged", "Alteration Issue", "Other"].map((r) => <option key={r}>{r}</option>)}
                  </select>
                </label>
              </div>
              <div className="border" style={{ borderColor: "var(--nylsu-border)" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: "var(--nylsu-table-head)" }}>
                      <th className="data-label px-4 py-2 text-left">SKU</th>
                      <th className="data-label px-4 py-2 text-right">{t("orders.qty")}</th>
                      <th className="data-label px-4 py-2 text-right">{t("returns.returnQty")}</th>
                      <th className="data-label px-4 py-2 text-left">{t("returns.disposition")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.items.map((si) => {
                      const sel = selectedItems[si.id] || {};
                      return (
                        <tr key={si.id} className="border-t" style={{ borderColor: "var(--nylsu-border)" }}>
                          <td className="px-4 py-2 font-mono text-xs">{si.sku}</td>
                          <td className="px-4 py-2 text-right font-mono text-xs">{si.quantity}</td>
                          <td className="px-4 py-2 text-right">
                            <input type="number" min="0" max={si.quantity} data-testid={`return-qty-${si.sku}`}
                              className="w-16 border py-1 px-2 text-center font-mono" style={{ borderColor: "var(--nylsu-border)" }}
                              value={sel.qty || 0}
                              onChange={(e) => setSelectedItems({ ...selectedItems, [si.id]: { ...sel, qty: e.target.value } })} />
                          </td>
                          <td className="px-4 py-2">
                            <select data-testid={`return-dispo-${si.sku}`} className="text-xs bg-transparent"
                              value={sel.disposition || "sellable"}
                              onChange={(e) => setSelectedItems({ ...selectedItems, [si.id]: { ...sel, disposition: e.target.value } })}>
                              {["sellable", "alteration", "damaged", "reject"].map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button type="submit" data-testid="return-submit"
                className="w-full py-3 text-xs uppercase tracking-[0.2em]" style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}>
                {t("returns.createCta")}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

/* ═════════════ Sales Dashboard ═════════════ */
export function SalesDashboardPage() {
  const { t, fmtCurrency } = useI18n();
  const [data] = useList("/dashboard/sales");
  const k = data?.kpis || {};
  const Stat = ({ label, value, tone, id }) => (
    <div className="border p-6" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }} data-testid={id}>
      <div className="data-label">{label}</div>
      <div className="font-serif text-3xl mt-3 font-light" style={{ color: tone }}>{value ?? "—"}</div>
    </div>
  );
  return (
    <div data-testid="sales-dashboard">
      <PageHeader eyebrow={t("sales.dashEyebrow")} title={t("sales.dashTitle")} subtitle={t("sales.dashSub")} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <Stat label={t("sales.kpi.todayNet")} value={fmtCurrency(k.today_net_sales || 0)} tone="var(--nylsu-gold)" id="kpi-today-net" />
        <Stat label={t("sales.kpi.todayUnits")} value={k.today_units} id="kpi-today-units" />
        <Stat label={t("sales.kpi.mtdNet")} value={fmtCurrency(k.mtd_net_sales || 0)} id="kpi-mtd-net" />
        <Stat label={t("sales.kpi.mtdMargin")} value={(k.mtd_gross_margin || 0).toFixed(1) + "%"} tone="var(--nylsu-success)" id="kpi-mtd-margin" />
        <Stat label={t("sales.kpi.mtdProfit")} value={fmtCurrency(k.mtd_gross_profit || 0)} tone="var(--nylsu-success)" id="kpi-mtd-profit" />
        <Stat label={t("sales.kpi.avgSale")} value={fmtCurrency(k.avg_sale_today || 0)} id="kpi-avg-sale" />
        <Stat label={t("sales.kpi.returns")} value={k.returns_total} tone="var(--nylsu-warning)" id="kpi-returns" />
        <Stat label={t("sales.kpi.failedInvoices")} value={k.failed_invoice_syncs} tone="var(--nylsu-danger)" id="kpi-failed-inv" />
      </div>

      {data?.top_styles?.length > 0 && (
        <div className="border p-6 mb-8" style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}>
          <div className="overline mb-3">{t("sales.topStyles")}</div>
          <div className="space-y-2">
            {data.top_styles.map((s) => (
              <div key={s._id} className="flex items-center justify-between text-sm">
                <span className="font-serif">{s.style_name || s._id}</span>
                <span className="font-mono text-xs">{s.units} · {fmtCurrency(s.net_sales)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
